import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Send, RefreshCw, BookOpen, Sparkles, HeartPulse, Compass, Feather, Moon, Sun, Quote, Lightbulb, BookOpenText, Mic, Headphones, Square, Trash2 } from "lucide-react";
import { usePreferences } from "../context/PreferencesContext";
import { ListenButton } from "./ListenButton";
import { ChatMarkdown } from "./ChatMarkdown";
import { fetchNarration, transcribeAudio, VOICE_OPTIONS } from "../lib/voice";
import { SUPPORTED_LANGUAGES } from "../i18n";

const MODE_META = [
  { id: "read", icon: BookOpenText },
  { id: "talk", icon: Mic },
  { id: "listen", icon: Headphones },
];

const SUGGESTION_ICONS = [Compass, Sparkles, Feather, BookOpen, HeartPulse, Moon, Sun, Quote, Lightbulb];

export default function StartHere() {
  const { t, i18n } = useTranslation();
  const { prefs, setPref } = usePreferences();
  const [sessionId, setSessionId] = useState("");
  const [history, setHistory] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [allowedPaths, setAllowedPaths] = useState([]);
  const messagesEndRef = useRef(null);
  const chatPanelRef = useRef(null);
  const userInteractedRef = useRef(false);
  const eventSourceRef = useRef(null);
  const conversationGenRef = useRef(0);
  const [confirmType, setConfirmType] = useState(null); // null | 'clear' | 'delete'

  // Abort any in-flight AI stream and invalidate its finaliser (used on clear/delete/new).
  const abortActiveStream = () => {
    conversationGenRef.current += 1;
    try { eventSourceRef.current?.close(); } catch (err) {}
    eventSourceRef.current = null;
    setIsStreaming(false);
  };
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const autoAudioRef = useRef(null);
  const autoUrlRef = useRef(null);

  const mode = prefs.mode || "read";
  const voice = prefs.voice || "male";
  const chatLang = (i18n.language || "en").split("-")[0];
  const prevLangRef = useRef(i18n.language);
  const modeRef = useRef(mode);
  const handsFreeRef = useRef(false);

  const suggestionTexts = t("startHere.suggestions", { returnObjects: true });
  const suggestions = (Array.isArray(suggestionTexts) ? suggestionTexts : []).map((text, i) => ({
    text,
    icon: SUGGESTION_ICONS[i % SUGGESTION_ICONS.length],
  }));

  const modes = MODE_META.map((m) => ({ ...m, label: t(`startHere.${m.id}`) }));
  const voiceOptions = VOICE_OPTIONS.map((v) => ({ id: v.id, label: t(`startHere.${v.id}`) }));

  useEffect(() => {
    let sId = localStorage.getItem("ttl-guide-session");
    if (!sId) {
      sId = "sess_" + Math.random().toString(36).substring(2, 11);
      localStorage.setItem("ttl-guide-session", sId);
    }
    setSessionId(sId);
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    const fetchHistory = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const response = await axios.get(`${backendUrl}/api/guide/session/${sessionId}`);
        setHistory(response.data.messages);
      } catch (err) {
        console.error("Error loading chat session:", err);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetchHistory();
  }, [sessionId]);

  // Verified internal destinations Thriver may link to — same source of truth as
  // the system prompt on the backend, so the renderer never diverges from what
  // the model was told.
  useEffect(() => {
    (async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const res = await axios.get(`${backendUrl}/api/guide/links?lang=${chatLang}`);
        setAllowedPaths(Array.isArray(res.data?.paths) ? res.data.paths : []);
      } catch (e) { /* silent — links simply won't render */ }
    })();
  }, [chatLang]);

  useEffect(() => {
    if (!userInteractedRef.current) return;
    const el = messagesEndRef.current;
    const container = el?.parentElement;
    if (container) container.scrollTop = container.scrollHeight;
  }, [history, isStreaming]);

  // Keep a live ref of the mode; leaving Talk mode stops any hands-free listening.
  useEffect(() => {
    modeRef.current = mode;
    if (mode !== "talk") {
      handsFreeRef.current = false;
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        try { mediaRecorderRef.current.stop(); } catch (e) {}
      }
    }
  }, [mode]);

  // Stop everything on unmount.
  useEffect(() => () => {
    handsFreeRef.current = false;
    try { if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") mediaRecorderRef.current.stop(); } catch (e) {}
    try { if (autoAudioRef.current) autoAudioRef.current.pause(); } catch (e) {}
  }, []);

  // When the interface language changes mid-session, begin a fresh conversation so
  // Thriver greets and replies entirely in the newly selected language — never a
  // mixed-language experience carried over from a previous session.
  useEffect(() => {
    if (prevLangRef.current === i18n.language) return;
    prevLangRef.current = i18n.language;
    if (history.length > 0) {
      const newSessionId = "sess_" + Math.random().toString(36).substring(2, 11);
      localStorage.setItem("ttl-guide-session", newSessionId);
      setSessionId(newSessionId);
      setHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const speakText = async (text) => {
    try {
      if (autoAudioRef.current) autoAudioRef.current.pause();
      if (autoUrlRef.current) URL.revokeObjectURL(autoUrlRef.current);
      const url = await fetchNarration((text || "").trim(), voice, chatLang);
      autoUrlRef.current = url;
      const audio = new Audio(url);
      audio.playbackRate = prefs.playbackSpeed || 1;
      autoAudioRef.current = audio;
      // Hands-free: after Thriver finishes speaking in Talk mode, listen again automatically.
      audio.onended = () => {
        if (modeRef.current === "talk") { handsFreeRef.current = true; startRecording(); }
      };
      audio.play();
    } catch (e) {
      console.error("Auto-narration error:", e);
      if (modeRef.current === "talk") { handsFreeRef.current = true; startRecording(); }
    }
  };

  const handleSubmit = async (e, textOverride = null) => {
    if (e) e.preventDefault();
    const query = textOverride || inputMessage;
    if (!query || !query.trim()) return;
    if (isStreaming) return;

    userInteractedRef.current = true;
    chatPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    const gen = conversationGenRef.current;
    const userMsg = { sender: "user", text: query.trim(), timestamp: new Date().toISOString() };
    setHistory(prev => [...prev, userMsg]);
    setInputMessage("");
    setIsStreaming(true);
    setHistory(prev => [...prev, { sender: "assistant", text: "", isStreaming: true }]);

    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const streamUrl = `${backendUrl}/api/guide/stream?session_id=${sessionId}&message=${encodeURIComponent(query.trim())}&lang=${chatLang}`;
    const shouldAutoSpeak = mode === "listen" || mode === "talk";

    let eventSource;
    let accumulatedText = "";
    let done = false;
    // Single, idempotent finaliser — guarantees the UI never gets stuck streaming,
    // regardless of DONE, connection error, or timeout.
    const finish = ({ fallback = false, refetch = false, speak = false } = {}) => {
      if (done) return;
      done = true;
      clearTimeout(safety);
      try { if (eventSource) eventSource.close(); } catch (err) {}
      if (eventSourceRef.current === eventSource) eventSourceRef.current = null;
      // If the conversation was cleared/deleted/reset while streaming, drop this
      // finaliser entirely so it can't repopulate the fresh/empty history.
      if (conversationGenRef.current !== gen) {
        setIsStreaming(false);
        return;
      }
      setIsStreaming(false);
      setHistory(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.sender === "assistant") {
          last.isStreaming = false;
          if (!last.text && fallback) last.text = t("startHere.unavailable");
        }
        return updated;
      });
      if (refetch) {
        axios.get(`${backendUrl}/api/guide/session/${sessionId}`)
          .then(res => setHistory(res.data.messages)).catch(() => {});
      }
      if (speak && shouldAutoSpeak && accumulatedText.trim()) speakText(accumulatedText.trim());
    };
    const safety = setTimeout(() => finish({ fallback: true }), 60000);

    try {
      eventSource = new EventSource(streamUrl);
      eventSourceRef.current = eventSource;
      eventSource.onmessage = (event) => {
        if (event.data === "[DONE]") {
          finish({ refetch: true, speak: true });
        } else {
          try {
            const textToken = JSON.parse(event.data);
            accumulatedText += textToken;
            setHistory(prev => {
              const updated = [...prev];
              const slot = updated[updated.length - 1];
              if (slot && slot.sender === "assistant") {
                slot.text = accumulatedText;
                slot.isStreaming = false;
              }
              return updated;
            });
          } catch (jsonErr) {
            console.error("Failed to parse token:", event.data);
          }
        }
      };
      eventSource.onerror = () => {
        // No text at all → surface a graceful message; otherwise treat as a normal close.
        finish({ fallback: !accumulatedText.trim(), speak: !!accumulatedText.trim() });
      };
    } catch (err) {
      console.error("Failed to open stream connection:", err);
      finish({ fallback: true });
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferred = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus", "audio/ogg"];
      const supported = (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported)
        ? preferred.find((tp) => MediaRecorder.isTypeSupported(tp))
        : null;
      const mr = supported ? new MediaRecorder(stream, { mimeType: supported }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || supported || "audio/webm" });
        if (!blob.size) { setIsTranscribing(false); return; }
        setIsTranscribing(true);
        try {
          const text = await transcribeAudio(blob, chatLang);
          if (text && text.trim()) handleSubmit(null, text.trim());
        } catch (err) {
          console.error("Transcription failed:", err);
          alert(t("startHere.micError"));
        } finally {
          setIsTranscribing(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setIsRecording(true);
    } catch (err) {
      console.error("Microphone unavailable:", err);
      alert(t("startHere.micError"));
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  const refetchHistory = async (sid) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const res = await axios.get(`${backendUrl}/api/guide/session/${sid}`);
      setHistory(res.data.messages);
    } catch (err) { console.error(err); }
    finally { setLoadingHistory(false); }
  };

  // Start a brand-new conversation (previous one is kept in the archive).
  const handleNewConversation = () => {
    abortActiveStream();
    setLoadingHistory(true);
    const newSessionId = "sess_" + Math.random().toString(36).substring(2, 11);
    localStorage.setItem("ttl-guide-session", newSessionId);
    setSessionId(newSessionId);
    setHistory([]);
    setLoadingHistory(false);
  };

  // Clear the messages of the current conversation (keeps the same conversation).
  const doClearConversation = async () => {
    abortActiveStream();
    setLoadingHistory(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      await axios.post(`${backendUrl}/api/guide/session/${sessionId}/clear`);
    } catch (err) { console.error(err); }
    setHistory([]);
    setLoadingHistory(false);
  };

  // Permanently delete the current conversation and open a fresh one.
  const doDeleteConversation = async () => {
    abortActiveStream();
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      await axios.delete(`${backendUrl}/api/guide/session/${sessionId}`);
    } catch (err) { console.error(err); }
    handleNewConversation();
  };

  // In-app confirmation (native window.confirm is blocked inside sandboxed iframes).
  const handleConfirmProceed = async () => {
    const type = confirmType;
    setConfirmType(null);
    if (type === "clear") await doClearConversation();
    else if (type === "delete") await doDeleteConversation();
  };

  return (
    <div className="py-12 space-y-16">

      {/* In-app confirmation dialog (iframe-safe replacement for window.confirm) */}
      {confirmType && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm px-6"
          data-testid="conversation-confirm-overlay"
          onClick={() => setConfirmType(null)}
        >
          <div
            className="bg-primary-custom border border-subtle-custom max-w-sm w-full p-8 space-y-7 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-serif text-base text-primary-custom leading-relaxed" data-testid="conversation-confirm-message">
              {confirmType === "delete" ? t("startHere.deleteConfirm") : t("startHere.clearConfirm")}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmType(null)}
                data-testid="confirm-cancel-btn"
                className="text-[10px] font-sans tracking-[0.2em] uppercase border border-subtle-custom px-5 py-2.5 text-secondary-custom hover:text-primary-custom transition-colors duration-300"
              >
                {t("startHere.cancel")}
              </button>
              <button
                onClick={handleConfirmProceed}
                data-testid="confirm-proceed-btn"
                className={`text-[10px] font-sans tracking-[0.2em] uppercase px-5 py-2.5 border transition-all duration-300 ${
                  confirmType === "delete"
                    ? "border-red-500/40 text-red-500 hover:bg-red-500 hover:text-white"
                    : "border-[var(--accent-bronze)] bg-[var(--accent-bronze)] text-white hover:opacity-90"
                }`}
              >
                {confirmType === "delete" ? t("startHere.deleteConversation") : t("startHere.clearConversation")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editorial Page Header */}
      <section className="max-w-4xl mx-auto px-6 text-center space-y-6">
        <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">
          {t("startHere.eyebrow")}
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-primary-custom tracking-tight leading-tight">
          {t("startHere.heading")}
        </h1>
        <p className="font-serif text-base sm:text-lg text-secondary-custom max-w-2xl mx-auto leading-relaxed font-light">
          {t("startHere.subtitle")}
        </p>
        <p className="font-sans text-[11px] text-secondary-custom max-w-xl mx-auto leading-relaxed" data-testid="ai-disclaimer">
          {t("startHere.disclaimer")}
        </p>
      </section>

      {/* Main Interactive Interface */}
      <section className="max-w-4xl mx-auto px-6 pb-24">
        <div
          ref={chatPanelRef}
          data-testid="ai-guide-container"
          className="border border-subtle-custom bg-surface-custom/30 rounded-none shadow-xl overflow-hidden flex flex-col h-[650px]"
        >

          {/* Companion Title bar */}
          <div className="bg-surface-custom border-b border-subtle-custom p-6 flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-2.5 h-2.5 rounded-full bg-[var(--accent-bronze)] animate-pulse" />
              <div className="flex flex-col">
                <span className="text-[10px] font-sans tracking-[0.2em] uppercase font-semibold text-primary-custom">
                  {t("startHere.titleName")}
                </span>
                <span className="text-[8px] font-sans tracking-widest text-secondary-custom uppercase">
                  {t("startHere.titleSub")}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2" data-testid="conversation-actions">
              <button
                onClick={handleNewConversation}
                data-testid="new-conversation-btn"
                title={t("startHere.newConversation")}
                className="text-[9px] font-sans tracking-widest uppercase border border-subtle-custom bg-primary-custom hover:bg-[var(--accent-bronze)] hover:text-white hover:border-[var(--accent-bronze)] transition-all duration-300 py-2 px-3 rounded-none"
              >
                {t("startHere.newConversation")}
              </button>
              <button
                onClick={() => setConfirmType("clear")}
                data-testid="clear-conversation-btn"
                title={t("startHere.clearConversation")}
                className="text-[9px] font-sans tracking-widest uppercase border border-subtle-custom bg-primary-custom hover:bg-primary-custom/70 text-secondary-custom hover:text-primary-custom transition-all duration-300 py-2 px-3 rounded-none"
              >
                {t("startHere.clearConversation")}
              </button>
              <button
                onClick={() => setConfirmType("delete")}
                data-testid="delete-conversation-btn"
                title={t("startHere.deleteConversation")}
                className="inline-flex items-center justify-center border border-subtle-custom bg-primary-custom text-secondary-custom hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/40 transition-all duration-300 p-2 rounded-none"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </div>

          {/* Voice Experience control bar */}
          <div className="bg-surface-custom/60 border-b border-subtle-custom px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-[8px] font-sans tracking-[0.25em] uppercase text-secondary-custom hidden sm:block">{t("startHere.experience")}</span>
              <div className="inline-flex border border-subtle-custom rounded-none overflow-hidden" data-testid="mode-toggle">
                {modes.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setPref("mode", m.id)}
                    data-testid={`mode-${m.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 text-[9px] font-sans tracking-[0.2em] uppercase transition-all duration-300 ${
                      mode === m.id
                        ? "bg-[var(--accent-bronze)] text-white"
                        : "bg-primary-custom text-secondary-custom hover:text-primary-custom"
                    }`}
                  >
                    <m.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-[8px] font-sans tracking-[0.25em] uppercase text-secondary-custom hidden sm:block">{t("startHere.voice")}</span>
              <div className="inline-flex border border-subtle-custom rounded-none overflow-hidden" data-testid="voice-toggle">
                {voiceOptions.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setPref("voice", v.id)}
                    data-testid={`voice-${v.id}`}
                    className={`px-3 py-2 text-[9px] font-sans tracking-[0.2em] uppercase transition-all duration-300 ${
                      voice === v.id
                        ? "bg-primary-custom text-[var(--accent-bronze)] border-[var(--accent-bronze)]"
                        : "bg-primary-custom text-secondary-custom hover:text-primary-custom"
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Conversational Screen */}
          <div className="flex-grow p-6 overflow-y-auto space-y-8 bg-primary-custom/20">
            {loadingHistory ? (
              <div className="h-full flex flex-col items-center justify-center text-secondary-custom space-y-4">
                <RefreshCw className="w-6 h-6 animate-spin text-[var(--accent-bronze)]" />
                <span className="font-serif italic text-xs">{t("startHere.preparing")}</span>
              </div>
            ) : (
              <>
                {history.length === 0 && (
                  <div className="flex justify-start" data-testid="thriver-welcome">
                    <div className="max-w-[80%] p-6 rounded-none bg-primary-custom border border-subtle-custom text-primary-custom">
                      <div className="text-[8px] font-sans tracking-widest uppercase text-secondary-custom mb-2">
                        {t("startHere.thriverLabel")}
                      </div>
                      <ChatMarkdown allowedPaths={allowedPaths}>{t("startHere.welcome")}</ChatMarkdown>
                      <div className="mt-4 pt-3 border-t border-subtle-custom">
                        <ListenButton text={t("startHere.welcome")} testId="welcome-listen" idleKey="listen.listen" />
                      </div>
                    </div>
                  </div>
                )}
                {history.map((msg, idx) => (
                  <div
                    key={idx}
                    data-testid={`chat-message-${msg.sender}`}
                    className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div className={`max-w-[80%] p-6 rounded-none ${
                      msg.sender === "user"
                        ? "bg-surface-custom border border-subtle-custom text-primary-custom"
                        : "bg-primary-custom border border-subtle-custom text-primary-custom"
                    }`}>
                      <div className="text-[8px] font-sans tracking-widest uppercase text-secondary-custom mb-2">
                        {msg.sender === "user" ? t("startHere.you") : t("startHere.thriverLabel")}
                      </div>
                      {msg.text ? (
                        <ChatMarkdown allowedPaths={allowedPaths}>{msg.text}</ChatMarkdown>
                      ) : (
                        msg.isStreaming && (
                          <span className="inline-flex items-center space-x-2 font-serif text-sm sm:text-base">
                            <span className="italic text-secondary-custom">{t("startHere.thinking")}</span>
                            <RefreshCw className="w-3.5 h-3.5 animate-spin text-[var(--accent-bronze)]" />
                          </span>
                        )
                      )}
                      {msg.sender === "assistant" && msg.text && !msg.isStreaming && (
                        <div className="mt-4 pt-3 border-t border-subtle-custom">
                          <ListenButton text={msg.text} testId={`chat-listen-${idx}`} idleKey="listen.listen" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* User input Form */}
          <div className="border-t border-subtle-custom bg-surface-custom p-6">
            <form onSubmit={(e) => handleSubmit(e)} className="flex items-center space-x-4">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                disabled={isStreaming || loadingHistory || isRecording || isTranscribing}
                placeholder={isTranscribing ? t("startHere.transcribing") : t("startHere.inputPlaceholder")}
                data-testid="ai-guide-input"
                className="flex-grow bg-transparent border-b border-focus-custom py-3 text-xs tracking-wider text-primary-custom uppercase outline-none focus:outline-none placeholder:text-secondary-custom/50"
              />

              {mode === "talk" && (
                <button
                  type="button"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isStreaming || loadingHistory || isTranscribing}
                  data-testid="voice-record-button"
                  className={`p-3 border transition-all duration-300 ${
                    isRecording
                      ? "bg-red-500 border-red-500 text-white animate-pulse"
                      : "border-subtle-custom bg-primary-custom text-secondary-custom hover:text-white hover:bg-[var(--accent-bronze)] hover:border-[var(--accent-bronze)]"
                  }`}
                  aria-label={isRecording ? "Stop recording" : "Start recording"}
                >
                  {isRecording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
              )}

              <button
                type="submit"
                disabled={isStreaming || loadingHistory || !inputMessage.trim()}
                data-testid="ai-guide-submit-button"
                className="p-3 border border-subtle-custom bg-primary-custom text-secondary-custom hover:text-primary-custom hover:border-[var(--accent-bronze)] hover:bg-[var(--accent-bronze)] hover:text-white transition-all duration-300"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
            {mode === "talk" && (
              <p className="mt-3 text-[9px] font-sans tracking-[0.15em] uppercase text-secondary-custom">
                {isRecording ? t("startHere.talkHintRecording") : isTranscribing ? t("startHere.talkHintTranscribing") : t("startHere.talkHintIdle")}
              </p>
            )}
            {mode === "listen" && (
              <p className="mt-3 text-[9px] font-sans tracking-[0.15em] uppercase text-secondary-custom">
                {t("startHere.listenHint")}
              </p>
            )}
          </div>

        </div>
      </section>

      {/* Suggested Prompt Cards */}
      <section className="max-w-4xl mx-auto px-6 space-y-8">
        <div className="text-center">
          <h3 className="text-lg font-serif text-primary-custom tracking-tight">
            {t("startHere.beginTitle")}
          </h3>
          <p className="font-serif text-xs italic text-secondary-custom">
            {t("startHere.beginSubtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {suggestions.map((sug, idx) => (
            <button
              key={idx}
              onClick={() => handleSubmit(null, sug.text)}
              disabled={isStreaming || loadingHistory}
              data-testid={`suggestion-card-${idx}`}
              className="border border-subtle-custom p-6 bg-surface-custom/30 text-left hover:border-[var(--accent-bronze)] transition-all duration-300 flex items-start space-x-4 rounded-none disabled:opacity-60"
            >
              <div className="w-8 h-8 border border-subtle-custom bg-primary-custom flex items-center justify-center shrink-0 mt-1">
                <sug.icon className="w-4 h-4 text-[var(--accent-bronze)]" strokeWidth={1} />
              </div>
              <div className="space-y-1">
                <span className="text-[8px] font-sans tracking-widest text-[var(--accent-bronze)] uppercase font-semibold block">
                  {t("startHere.startingQuestion")}
                </span>
                <span className="font-serif text-sm text-primary-custom leading-snug">
                  {sug.text}
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

    </div>
  );
}
